// @ts-nocheck
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { View, UserSettings, ChildLearningPlan, PlanStatus, LearningHistory, PlanDifficulty, PlanDuration, Child, Assignment, Homework, Submission, Class, Student } from '../types';
import { generateChildLearningPlan } from '../services/apiService';
import Card from './common/Card';
import Spinner from './common/Spinner';
import LoadingDataIndicator from './common/LoadingDataIndicator';
import { ArrowLeftIcon, ArrowPathIcon, TrophyIcon, SparklesIcon, XMarkIcon, TrashIcon, CheckIcon, PlayIcon, CheckBadgeIcon, ClockIcon, FlagIcon, ChevronDownIcon, UploadIcon, BellIcon, ClipboardDocumentListIcon, PencilIcon, AcademicCapIcon, ChartBarIcon, ChatBubbleBottomCenterTextIcon, CalendarIcon, PlusIcon, UserPlusIcon, UserCircleIcon } from './common/Icons';
import { CreditCost } from '../config';
import { getCreditCostSuffix } from '../utils/creditUi';
import { getDashboardGreeting, getDashboardSubtitle } from '../utils/dashboardGreetings';
import { getStatusDisplay, getLastSubmission, isSubmissionGraded, isSubmissionSubmitted, SUBMISSION_STATUS } from '../utils/assignmentUtils';

import { getUserLimits, checkLimit } from '../utils/limitUtils';

import { BeakerIcon, ShieldCheckIcon, MagnifyingGlassIcon, BookOpenIcon } from './common/Icons';

import HomeworkViewer from './common/HomeworkViewer';
import HelpOnboarding, { HelpStep } from './common/HelpOnboarding';
import StudyAssistantModal from './common/StudyAssistantModal';

const studentHelpSteps: HelpStep[] = [
    {
        title: 'Học tập cùng AI',
        description: 'Sử dụng trợ lý học tập thông minh để giải đáp thắc mắc và luyện phát âm English Lab.',
        icon: <SparklesIcon className="h-12 w-12 text-sky-600" />
    },
    {
        title: 'Lộ trình Cá nhân',
        description: 'Tạo lộ trình học tập riêng biệt dựa trên mục tiêu và tài liệu cá nhân để tối ưu hóa việc học.',
        icon: <AcademicCapIcon className="h-12 w-12 text-sky-600" />
    },
    {
        title: 'Nhiệm vụ & Bài tập',
        description: 'Nhận nhiệm vụ từ thầy cô, hoàn thành bài tập tương tác và theo dõi điểm số tức thì.',
        icon: <ClipboardDocumentListIcon className="h-12 w-12 text-sky-600" />
    },
    {
        title: 'Theo dõi Kết quả',
        description: 'Xem biểu đồ tiến độ, tỉ lệ chuyên cần và nhận xét của thầy cô qua từng buổi học.',
        icon: <ChartBarIcon className="h-12 w-12 text-sky-600" />
    }
];

function formatBytes(bytes: number, decimals = 2): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

const LearningPlanModal: React.FC<{
    student: UserSettings;
    onClose: () => void;
    onSave: (plan: Omit<ChildLearningPlan, 'id'>) => void;
    onCreditUsage: (cost: number, actionName: string) => boolean;
}> = ({ student, onClose, onSave, onCreditUsage }) => {
    const [goal, setGoal] = useState('');
    const [difficulty, setDifficulty] = useState<PlanDifficulty>(PlanDifficulty.Medium);
    const [duration, setDuration] = useState<PlanDuration>(PlanDuration.Short);
    const [isLoading, setIsLoading] = useState(false);
    const mainContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isLoading && mainContainerRef.current) {
            mainContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [isLoading]);
    const [error, setError] = useState('');
    const [generatedPlan, setGeneratedPlan] = useState<Pick<ChildLearningPlan, 'title' | 'dailyPlans'> | null>(null);

    const [files, setFiles] = useState<File[]>([]);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const MAX_FILES = 10;
    const MAX_FILE_SIZE_MB = 2;


    const handleSampleData = () => {
        let sampleGoal = '';
        const studentAge = student.age || 15; // default age if not set
        if (studentAge < 6) {
            sampleGoal = `Giúp em làm quen với các chữ cái và số đếm từ 1 đến 10 qua các trò chơi vui nhộn.`;
        } else if (studentAge >= 6 && studentAge < 10) {
            sampleGoal = `Cùng em rèn luyện kỹ năng đọc hiểu truyện ngắn và luyện tập các phép toán cộng trừ trong phạm vi 100.`;
        } else {
            sampleGoal = `Giúp em cải thiện kỹ năng viết đoạn văn ngắn (3-5 câu) và học 20 từ vựng tiếng Anh mới về chủ đề thiên nhiên.`;
        }
        setGoal(sampleGoal);
        setError('');
        setGeneratedPlan(null);
    };

    const addFiles = (newFiles: File[]) => {
        setError(''); 
        let validFiles: File[] = [];
        if (files.length + newFiles.length > MAX_FILES) {
            setError(`Bạn chỉ có thể tải lên tối đa ${MAX_FILES} tệp.`);
            return;
        }

        for (const file of newFiles) {
            if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
                setError(`Tệp "${file.name}" quá lớn (tối đa ${MAX_FILE_SIZE_MB}MB).`);
                return;
            }
            validFiles.push(file);
        }
        setFiles(prev => [...prev, ...validFiles]);
    };
    
    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            addFiles(Array.from(e.target.files));
        }
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        if (e.dataTransfer.files) {
            addFiles(Array.from(e.dataTransfer.files));
        }
    };

    const removeFile = (index: number) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleGenerate = async () => {
        if (!goal.trim()) {
            setError('Vui lòng nhập mục tiêu học tập.');
            return;
        }
        if (!onCreditUsage(CreditCost.CHILD_PLAN ?? CreditCost.ACTIVITY, `Tạo lộ trình học tập cho ${student.teacherName}`)) {
            setError(`Không đủ ${CREDIT.toLowerCase()} để thực hiện. Vui lòng nạp thêm.`);
            return;
        }
        setError('');
        setIsLoading(true);
        setGeneratedPlan(null);
        try {
            const fileReaders = files
                .filter(file => file.type === 'text/plain')
                .map(file => new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = (e) => resolve(`--- BẮT ĐẦU TỆP: ${file.name} ---\n${e.target?.result}\n--- KẾT THÚC TỆP ---\n\n`);
                    reader.onerror = () => reject(new Error(`Lỗi đọc tệp ${file.name}`));
                    reader.readAsText(file);
                }));

            const fileContents = await Promise.all(fileReaders);
            const fileContext = fileContents.length > 0 ? fileContents.join('') : null;
            
            const plan = await generateChildLearningPlan(student.age || 15, student.gender || 'other', goal, difficulty, duration, fileContext);
            setGeneratedPlan(plan);
        } catch (err) {
            setError((err as Error).message || 'Không thể tạo kế hoạch. Vui lòng thử lại.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSavePlan = () => {
        if (generatedPlan) {
            onSave({
                childId: student.email, // Use email as unique ID for student
                goal,
                difficulty,
                duration,
                createdAt: new Date().toISOString(),
                planStatus: PlanStatus.OnTrack,
                ...generatedPlan,
            });
            onClose();
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60" onClick={onClose}>
            <div ref={mainContainerRef} className="bg-white rounded-xl shadow-2xl w-full max-w-3xl m-4 flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
                <div className="p-6 border-b flex justify-between items-center">
                    <h3 className="text-xl font-bold text-gray-900">Tạo Lộ trình Học tập cho {student.teacherName}</h3>
                    <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-200"><XMarkIcon className="h-5 w-5 text-gray-600" /></button>
                </div>
                
                <div className="p-6 flex-grow overflow-y-auto space-y-6 text-left">
                    <div>
                        <div className="flex justify-between items-center">
                             <label htmlFor="goal" className="block text-sm font-semibold text-gray-700 mb-1">1. Đặt mục tiêu cho bạn</label>
                             <button onClick={handleSampleData} className="text-xs font-medium text-teal-600 hover:text-teal-800">Sử dụng dữ liệu mẫu</button>
                        </div>
                        <p className="text-xs text-gray-500 mb-2">Mục tiêu càng cụ thể, lộ trình học AI tạo ra càng phù hợp.</p>
                        <textarea id="goal" value={goal} onChange={e => setGoal(e.target.value)} placeholder="Ví dụ: Học thuộc bảng cửu chương 2 và 3, hoặc Cải thiện kỹ năng đọc hiểu tiếng Anh qua truyện tranh..." rows={3} className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1 text-left">2. Cung cấp tài liệu tham khảo (Tùy chọn)</label>
                        <p className="text-xs text-gray-500 mb-2 text-left">Tải lên tài liệu (.txt, .pdf, .doc) để AI có thêm ngữ cảnh cho việc tạo lộ trình.</p>
                        
                        <div 
                            onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }}
                            onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); }}
                            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                            onDrop={handleDrop}
                            className={`relative flex flex-col items-center justify-center w-full border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer hover:border-gray-400 ${isDragging ? 'border-teal-500 bg-teal-50' : 'border-gray-300'}`}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <UploadIcon className="mx-auto h-8 w-8 text-gray-400" />
                            <p className="mt-2 block text-sm font-medium text-gray-900">Kéo thả hoặc <span className="text-teal-600">chọn tệp</span></p>
                            <p className="mt-1 text-xs text-gray-500 text-center">Hỗ trợ .txt, .pdf, .doc, .docx. Tối đa 10 tệp, mỗi tệp &lt; 2MB.</p>
                            <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileSelect} accept=".txt,.pdf,.doc,.docx" />
                        </div>
                        
                        {files.length > 0 && (
                            <div className="mt-3 space-y-2 max-h-24 overflow-y-auto pr-2">
                                {files.map((file, index) => (
                                    <div key={index} className="flex items-center justify-between p-2 bg-gray-100 rounded-md text-sm">
                                        <div className="flex items-center gap-2 overflow-hidden">
                                            <span className="font-medium truncate">{file.name}</span>
                                            <span className="text-gray-500 flex-shrink-0">{formatBytes(file.size)}</span>
                                        </div>
                                        <button onClick={() => removeFile(index)} className="p-1 text-gray-500 hover:text-red-600 rounded-full"><XMarkIcon className="h-4 w-4"/></button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    
                    {error && <p className="text-sm text-red-500 mt-1">{error}</p>}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2 text-left">3. Chọn mức độ khó</label>
                            <div className="space-y-2">
                                {Object.values(PlanDifficulty).map((level: PlanDifficulty) => (
                                    <label key={level} className={`flex items-center p-3 border rounded-lg cursor-pointer ${difficulty === level ? 'bg-teal-50 border-teal-400' : 'bg-white'}`}>
                                        <input type="radio" name="difficulty" value={level} checked={difficulty === level} onChange={() => setDifficulty(level)} className="h-4 w-4 text-teal-600 focus:ring-teal-500"/>
                                        <span className="ml-3 text-sm font-medium text-gray-800">{level}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                         <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2 text-left">4. Chọn thời lượng</label>
                            <div className="space-y-2">
                                {Object.values(PlanDuration).map((d: PlanDuration) => (
                                    <label key={d} className={`flex items-center p-3 border rounded-lg cursor-pointer ${duration === d ? 'bg-teal-50 border-teal-400' : 'bg-white'}`}>
                                        <input type="radio" name="duration" value={d} checked={duration === d} onChange={() => setDuration(d)} className="h-4 w-4 text-teal-600 focus:ring-teal-500"/>
                                        <span className="ml-3 text-sm font-medium text-gray-800">{d}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="text-center">
                        <button onClick={handleGenerate} disabled={isLoading || !goal.trim()} className="flex items-center justify-center gap-2 px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 disabled:bg-gray-400 transition-colors">
                            {isLoading ? <Spinner size="sm"/> : <SparklesIcon className="h-5 w-5" />}
                            <span>{isLoading ? 'AI đang thiết kế...' : `5. Nhờ AI thiết kế lộ trình${getCreditCostSuffix(CreditCost.LESSON_PLAN)}`}</span>
                        </button>
                    </div>

                    {generatedPlan && (
                        <div className="p-4 border-2 border-dashed border-green-300 bg-green-50 rounded-lg text-left">
                            <h4 className="text-lg font-bold text-green-800">Lộ trình được đề xuất: "{generatedPlan.title}"</h4>
                             <div className="mt-4 space-y-3 text-sm text-gray-700">
                                {generatedPlan.dailyPlans.map((day, dayIndex) => (
                                    <div key={dayIndex}>
                                        <p className="font-semibold">{day.day}:</p>
                                        <ul className="list-disc list-inside pl-4">
                                            {day.modules.map((module, moduleIndex) => <li key={moduleIndex}>{module.title}</li>)}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 rounded-b-xl">
                    <button onClick={onClose} className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">Hủy</button>
                    <button onClick={handleSavePlan} disabled={!generatedPlan} className="px-5 py-2 text-sm font-semibold text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:bg-gray-400 flex items-center gap-2">
                        <CheckIcon className="h-4 w-4" />
                        <span>Lưu Lộ trình</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

// FIX: Define the missing StudentDashboardProps interface.
interface StudentDashboardProps {
    onNavigate: (view: View, payload?: object) => void;
    student: UserSettings;
    joinRequests?: any[];
    learningPlans: ChildLearningPlan[];
    assignments: Assignment[];
    homeworks: Homework[];
    submissions: Submission[];
    notifications?: Notification[];
    classes: Class[];
    students: Student[];
    onSavePlan: (plan: Omit<ChildLearningPlan, 'id'>) => ChildLearningPlan;
    onDeletePlan: (id: string) => void;
    onStudentSubmit: (assignmentId: string, studentId: string) => void;
    onUpdateSettings: (update: React.SetStateAction<UserSettings | null>) => void;
    onCreditUsage: (cost: number, actionName: string) => boolean;
    onRefreshData?: () => void;
    onAddNotification?: (notif: Partial<Notification>, showToast?: boolean) => void;
    isLoading?: boolean;
}

const StudentDashboard: React.FC<StudentDashboardProps> = (props) => {
    const navigate = useNavigate();
    const { onNavigate, student, joinRequests = [], learningPlans, assignments, homeworks, submissions, notifications = [], onSavePlan, onDeletePlan, onStudentSubmit, onUpdateSettings, onCreditUsage, classes, students, onRefreshData, onAddNotification, isLoading } = props;
    const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
    const [isAssistantOpen, setIsAssistantOpen] = useState(false);
    
    const limits = getUserLimits(student);
    const [activeHomework, setActiveHomework] = useState<{assignmentId: string, homework: Homework} | null>(null);
    const [collapsedPlans, setCollapsedPlans] = useState<Record<string, boolean>>({});

    const aiTools = [
        { name: 'Thiết kế thông minh', description: 'Tạo poster, học liệu và các mẫu thiết kế AI', icon: <SparklesIcon className="h-6 w-6" />, view: View.DesignCenter, color: 'bg-violet-500' },
        { name: 'Tạo ảnh AI', description: 'Biến ý tưởng thành hình ảnh trực quan', icon: <SparklesIcon className="h-6 w-6" />, view: View.Drawing, color: 'bg-pink-500' },
        { name: 'Video tương tác', description: 'Học tập chủ động với câu hỏi lồng ghép trong video', icon: <PlayIcon className="h-6 w-6" />, view: View.InteractiveVideo, color: 'bg-rose-500' },
        { name: 'Phòng Lab AI', description: 'Thực hành huấn luyện và thử nghiệm mô hình AI', icon: <BeakerIcon className="h-6 w-6" />, view: View.AILab, color: 'bg-purple-500' },
        { name: 'Thử thách Đạo đức', description: 'Tình huống thực tế về an toàn và đạo đức AI', icon: <ShieldCheckIcon className="h-6 w-6" />, view: View.AIEthics, color: 'bg-blue-500' },
        { name: 'Kiểm chứng AI', description: 'Rèn luyện tư duy phản biện với nội dung AI', icon: <MagnifyingGlassIcon className="h-6 w-6" />, view: View.AIFactCheck, color: 'bg-emerald-500' },
        { name: 'Hành trình khám phá', description: 'Khám phá nghề nghiệp & lộ trình học tập phù hợp với bạn', icon: <FlagIcon className="h-6 w-6" />, view: View.CareerJourney, color: 'bg-violet-500' },
    ];

    const resolvedStudentProfile = useMemo(() => {
        const linkedStudentId = String((student as any).studentId || '').trim();
        if (linkedStudentId) {
            const byId = students.find(s => String(s.id) === linkedStudentId);
            if (byId) return byId;
        }

        if (student.email) {
            const byEmail = students.find(s => !!s.email && s.email === student.email);
            if (byEmail) return byEmail;
        }

        return null;
    }, [students, student.email, (student as any).studentId]);

    const studentIdentityCandidates = useMemo(() => {
        const ids = new Set<string>();
        if ((student as any).studentId) ids.add(String((student as any).studentId));
        if (resolvedStudentProfile?.id) ids.add(String(resolvedStudentProfile.id));
        if (resolvedStudentProfile?.studentId) ids.add(String(resolvedStudentProfile.studentId));
        if (student.email) ids.add(String(student.email));
        if (resolvedStudentProfile?.email) ids.add(String(resolvedStudentProfile.email));
        return Array.from(ids).filter(Boolean);
    }, [student.email, (student as any).studentId, resolvedStudentProfile]);

    const approvedClassIds = useMemo(() => {
        return (joinRequests || [])
            .filter((r: any) => r.status === 'approved')
            .map((r: any) => String(r.class_id ?? r.classId))
            .filter(Boolean);
    }, [joinRequests]);

    const myProfile = useMemo(() => {
        return resolvedStudentProfile;
    }, [resolvedStudentProfile]);

    const myClasses = useMemo(() => {
        if (!myProfile) return [];
        return classes.filter(c => {
            const classId = String(c.id);
            const isMember = (c.studentIds || []).some((sid: any) => studentIdentityCandidates.includes(String(sid)));
            const isApproved = approvedClassIds.includes(classId);
            return isMember || isApproved;
        });
    }, [approvedClassIds, classes, myProfile, studentIdentityCandidates]);

    const stats = useMemo(() => {
        if (!myProfile || myClasses.length === 0) return { avgGrade: '---', attendance: '---' };
        
        const allSessions = myClasses.flatMap(c => c.sessions || []);
        const mySessions = allSessions.filter(s => (s.grades || []).some(g => String(g.studentId) === String(myProfile.id)));
        
        const grades = mySessions
            .map(s => (s.grades || []).find(g => String(g.studentId) === String(myProfile.id))?.grade)
            .filter(g => g !== '' && g !== undefined)
            .map(g => parseFloat(g as string) || 0);
            
        const avgGrade = grades.length > 0 ? (grades.reduce((a, b) => a + b, 0) / grades.length).toFixed(1) : '---';
        const attendance = mySessions.length > 0 
            ? (mySessions.filter(s => (s.grades || []).find(g => String(g.studentId) === String(myProfile.id))?.attendance === 'present').length / mySessions.length * 100).toFixed(0)
            : '---';
            
        return { avgGrade, attendance };
    }, [myClasses, myProfile]);

    const getLatestSubmissionForCurrentStudent = useMemo(() => {
        return (assignmentId: string) => {
            const matches = studentIdentityCandidates
                .map(candidate => getLastSubmission(submissions, assignmentId, candidate))
                .filter(Boolean);

            if (matches.length === 0) return undefined;
            if (matches.length === 1) return matches[0];

            return matches.sort((a: any, b: any) => {
                const aDate = new Date(a?.submittedDate || a?.submitted_date || 0).getTime();
                const bDate = new Date(b?.submittedDate || b?.submitted_date || 0).getTime();
                return bDate - aDate;
            })[0];
        };
    }, [studentIdentityCandidates, submissions]);

    useEffect(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const initialCollapsed: Record<string, boolean> = {};
        learningPlans.forEach(plan => {
            if (plan.startDate) {
                const planStartDate = new Date(plan.startDate);
                planStartDate.setHours(0, 0, 0, 0);
                if (planStartDate > today) {
                    initialCollapsed[plan.id] = true;
                }
            }
        });
        setCollapsedPlans(initialCollapsed);
    }, [learningPlans]);

    // State for editable goals
    const [isEditingGoals, setIsEditingGoals] = useState(false);
    const [shortTerm, setShortTerm] = useState(student.shortTermGoals || "");
    const [longTerm, setLongTerm] = useState(student.longTermGoals || "");

    const handleSaveGoals = () => {
        onUpdateSettings(prev => ({ ...prev!, shortTermGoals: shortTerm, longTermGoals: longTerm }));
        setIsEditingGoals(false);
    };
    
    const studentAssignments = useMemo(() => {
        const myClassesForAssign = classes.filter(c => {
            const classId = String(c.id);
            const isMember = (c.studentIds || []).some((sid: any) => studentIdentityCandidates.includes(String(sid)));
            const isApproved = approvedClassIds.includes(classId);
            return isMember || isApproved;
        });
        
        return assignments
            .filter(a => {
                const aClassId = String(a.classId ?? (a as any).class_id ?? '');
                const isMyClass = myClassesForAssign.some(c => String(c.id) === aClassId);
                const aStudentIds = (a.studentIds || (a as any).student_ids || []) as any[];
                const isForMe = aStudentIds.length === 0 || aStudentIds.some((sid: any) => studentIdentityCandidates.includes(String(sid)));
                const scheduledDate = a.scheduledDate || (a as any).scheduled_date;
                const isStarted = !scheduledDate || new Date(scheduledDate) <= new Date();
                
                return isMyClass && isForMe && isStarted && a.status !== 'recalled';
            })
            .map(assignment => {
                const hwId = assignment.homeworkId || assignment.id;
                const homework = homeworks.find(h => String(h.id) === String(hwId));
                const submission = getLatestSubmissionForCurrentStudent(String(assignment.id));
                const dueDate = assignment.dueDate || (assignment as any).due_date;
                const dueTime = dueDate ? new Date(dueDate).getTime() : 0;
                const isExpired = dueTime > 0 && dueTime < new Date().getTime();
                const canSubmit = !isExpired || assignment.allowLateSubmission;
                const cls = classes.find(c => String(c.id) === String(assignment.classId || (assignment as any).class_id));

                return {
                    ...assignment,
                    title: (assignment as any).homeworkTitle || (assignment as any).homework_title || homework?.title || assignment.title || 'Bài tập không xác định',
                    className: cls?.name || 'Lớp học',
                    status: submission?.status || SUBMISSION_STATUS.CHUA_NOP,
                    grade: submission?.grade,
                    teacherComments: submission?.teacherComments,
                    canSubmit,
                    isExpired
                };
            });
    }, [approvedClassIds, assignments, classes, getLatestSubmissionForCurrentStudent, homeworks, studentIdentityCandidates]);

    const getAssignmentStatusMeta = useMemo(() => {
        return (assignment: any) => {
            const submission = getLatestSubmissionForCurrentStudent(String(assignment.id));
            const isAnnouncement = assignment.type === 'announcement';
            const isTask = assignment.type === 'task';

            if (isAnnouncement) {
                return submission && isSubmissionSubmitted(submission.status)
                    ? { label: 'Đã xem', badgeClass: 'bg-slate-100 text-slate-700', cardClass: 'bg-slate-50 border-slate-200' }
                    : { label: 'Chưa xem', badgeClass: 'bg-teal-100 text-teal-700', cardClass: 'bg-white border-slate-100' };
            }

            if (!submission) {
                return {
                    label: isTask ? 'Chưa xác nhận' : 'Chưa làm',
                    badgeClass: 'bg-amber-100 text-amber-700',
                    cardClass: 'bg-white border-slate-100'
                };
            }

            if (isSubmissionGraded(submission.status)) {
                return { label: 'Đã chấm', badgeClass: 'bg-emerald-100 text-emerald-700', cardClass: 'bg-slate-50 border-emerald-100' };
            }

            if (isSubmissionSubmitted(submission.status)) {
                return {
                    label: isTask ? 'Đã xác nhận' : 'Đã nộp',
                    badgeClass: 'bg-blue-100 text-blue-700',
                    cardClass: 'bg-slate-50 border-emerald-100'
                };
            }

            return { label: 'Đang làm', badgeClass: 'bg-amber-100 text-amber-700', cardClass: 'bg-white border-slate-100' };
        };
    }, [getLatestSubmissionForCurrentStudent]);

    const announcementAssignments = useMemo(() => studentAssignments.filter(a => (a as any).type === 'announcement'), [studentAssignments]);
    const actionableAssignments = useMemo(() => studentAssignments.filter(a => (a as any).type !== 'announcement'), [studentAssignments]);
    const activeAssignments = useMemo(() => actionableAssignments.filter(a => !isSubmissionSubmitted(a.status)), [actionableAssignments]);
    const completedAssignments = useMemo(() => actionableAssignments.filter(a => isSubmissionSubmitted(a.status)), [actionableAssignments]);
    const studentAvgGrade = useMemo(() => {
        const graded = actionableAssignments.filter(a => isSubmissionGraded(a.status) && a.grade != null);
        if (graded.length === 0) return '---';
        return (graded.reduce((acc, a) => acc + (parseFloat(String(a.grade)) || 0), 0) / graded.length).toFixed(1);
    }, [actionableAssignments]);

    const unreadNotifCount = useMemo(() => {
        const unseenAnnouncementIds = announcementAssignments
            .filter(a => !isSubmissionSubmitted(a.status))
            .map(a => `assignment_${a.id}`);
        const unreadNotificationIds = notifications.filter(n => !n.isRead).map(n => `notification_${n.id}`);
        return new Set([...unseenAnnouncementIds, ...unreadNotificationIds]).size;
    }, [announcementAssignments, notifications]);

    if (activeHomework) {
        const assignment = assignments.find(a => a.id === activeHomework.assignmentId);
        const assignType = (assignment as any)?.type;
        return <HomeworkViewer 
            homework={activeHomework.homework} 
            onClose={() => setActiveHomework(null)} 
            initialAnswers={activeHomework.initialAnswers}
            timeLimitMinutes={assignment?.timeLimitMinutes}
            assignmentType={assignType === 'task' ? 'task' : assignType === 'announcement' ? 'announcement' : 'homework'}
            user={student}
            onComplete={(results) => {
                const sId = resolvedStudentProfile?.id || ((student as any)?.studentId || (student as any)?.email);
                if (sId) {
                    onStudentSubmit(activeHomework.assignmentId, sId, results);
                }
                setActiveHomework(null);
            }} 
        />;
    }

    /* if (isLoading) {
        return <LoadingDataIndicator />;
    } */

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
            <HelpOnboarding steps={studentHelpSteps} role="student" />
            
            {/* Intelligent Welcome Banner */}
            <section className="relative overflow-hidden bg-gradient-to-br from-sky-600 via-sky-700 to-indigo-800 rounded-[2rem] lg:rounded-[2.5rem] p-5 lg:p-8 text-white shadow-xl">
                <div className="relative z-10 flex flex-row items-center justify-between gap-4 lg:gap-8">
                    <div className="space-y-2 lg:space-y-4 text-left flex-1 min-w-0">
                        <div className="inline-flex items-center gap-2 px-2.5 py-0.5 bg-white/20 backdrop-blur-md rounded-full text-[8px] lg:text-[10px] font-black uppercase tracking-widest border border-white/10">
                            <SparklesIcon className="h-3 w-3 lg:h-3.5 lg:w-3.5 text-sky-300" />
                            <span>Không gian học tập</span>
                        </div>
                        <h2 className="text-lg md:text-2xl lg:text-3xl font-black tracking-tight text-left line-clamp-2 md:line-clamp-3 leading-tight">
                            {getDashboardGreeting('student', student.teacherName || student.email || '', student.greeting)}
                        </h2>
                        <p className="text-sky-100 font-medium max-w-xl text-[11px] lg:text-base leading-snug text-left opacity-90 line-clamp-2 hidden sm:block">
                            {activeAssignments.length > 0 
                                ? `Bạn còn ${activeAssignments.length} nhiệm vụ cần làm. Bắt đầu ngay nhé!` 
                                : "Tuyệt vời! Bạn đã hoàn thành tất cả nhiệm vụ. Phát huy nhé!"}
                        </p>
                        <div className="flex flex-wrap gap-2 md:gap-3 pt-1">
                            <div className="px-2 py-1 md:px-3 md:py-2 bg-white/15 backdrop-blur-sm rounded-lg lg:rounded-xl border border-white/10 flex items-center gap-1.5 md:gap-2.5 shadow-sm hover:bg-white/20 transition-all cursor-default" title="Điểm trung bình học tập">
                                <div className="w-5 h-5 lg:w-8 lg:h-8 bg-sky-500 rounded-md lg:rounded-xl flex items-center justify-center shadow-lg text-white shrink-0">
                                    <AcademicCapIcon className="h-3 w-3 lg:h-5 lg:w-5" />
                                </div>
                                <div className="text-left">
                                    <p className="text-[6px] lg:text-[9px] font-black text-sky-200 uppercase tracking-widest leading-none mb-0.5 text-left">Điểm TB</p>
                                    <p className="text-xs lg:text-lg font-black leading-none text-left">{stats.avgGrade !== '---' ? stats.avgGrade : studentAvgGrade}</p>
                                </div>
                            </div>
                            <div className="px-2 py-1 md:px-3 md:py-2 bg-white/15 backdrop-blur-sm rounded-lg lg:rounded-xl border border-white/10 flex items-center gap-1.5 md:gap-2.5 shadow-sm hover:bg-white/20 transition-all cursor-default" title="Tỉ lệ chuyên cần">
                                <div className="w-5 h-5 lg:w-8 lg:h-8 bg-indigo-500 rounded-md lg:rounded-xl flex items-center justify-center shadow-lg text-white shrink-0">
                                    <CalendarIcon className="h-3 w-3 lg:h-5 lg:w-5" />
                                </div>
                                <div className="text-left">
                                    <p className="text-[6px] lg:text-[9px] font-black text-indigo-200 uppercase tracking-widest leading-none mb-0.5 text-left">Chuyên cần</p>
                                    <p className="text-xs lg:text-lg font-black leading-none text-left">{stats.attendance}{stats.attendance !== '---' ? '%' : ''}</p>
                                </div>
                            </div>
                            <div className="px-2 py-1 md:px-3 md:py-2 bg-white/15 backdrop-blur-sm rounded-lg lg:rounded-xl border border-white/10 flex items-center gap-1.5 md:gap-2.5 shadow-sm hover:bg-white/20 transition-all cursor-default" title="Số lượng thông báo mới">
                                <div className="w-5 h-5 lg:w-8 lg:h-8 bg-rose-500 rounded-md lg:rounded-xl flex items-center justify-center shadow-lg text-white shrink-0">
                                    <BellIcon className="h-3 w-3 lg:h-5 lg:w-5" />
                                </div>
                                <div className="text-left">
                                    <p className="text-[6px] lg:text-[9px] font-black text-rose-200 uppercase tracking-widest leading-none mb-0.5 text-left">Thông báo</p>
                                    <p className="text-xs lg:text-lg font-black leading-none text-left">{unreadNotifCount}</p>
                                </div>
                            </div>
                            <div className="px-2 py-1 md:px-3 md:py-2 bg-white/15 backdrop-blur-sm rounded-lg lg:rounded-xl border border-white/10 flex items-center gap-1.5 md:gap-2.5 shadow-sm hover:bg-white/20 transition-all cursor-default" title="Số nhiệm vụ đã hoàn thành">
                                <div className="w-5 h-5 lg:w-8 lg:h-8 bg-emerald-500 rounded-md lg:rounded-xl flex items-center justify-center shadow-lg text-white shrink-0">
                                    <CheckBadgeIcon className="h-3 w-3 lg:h-5 lg:w-5" />
                                </div>
                                <div className="text-left">
                                    <p className="text-[6px] lg:text-[9px] font-black text-emerald-200 uppercase tracking-widest leading-none mb-0.5 text-left">Xong</p>
                                    <p className="text-xs lg:text-lg font-black leading-none text-left">{completedAssignments.length}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Progress Circle Visual */}
                    <div className="shrink-0 flex flex-col items-center justify-center p-3 lg:p-6 bg-white/10 backdrop-blur-xl rounded-2xl lg:rounded-[2.5rem] border border-white/20 shadow-2xl">
                        <div className="relative w-16 h-16 md:w-20 md:h-20 lg:w-28 lg:h-28">
                            <svg className="w-full h-full transform -rotate-90">
                                <circle cx="50%" cy="50%" r="42%" stroke="currentColor" strokeWidth="5" fill="transparent" className="text-white/10" />
                                <circle 
                                    cx="50%" cy="50%" r="42%" stroke="currentColor" strokeWidth="5" fill="transparent" 
                                    strokeDasharray="264" 
                                    strokeDashoffset={264 - (actionableAssignments.length > 0 ? (completedAssignments.length / actionableAssignments.length) * 264 : 0)} 
                                    className="text-sky-400 transition-all duration-1000 ease-in-out" 
                                    strokeLinecap="round"
                                />
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                <span className="text-sm lg:text-2xl font-black">{actionableAssignments.length > 0 ? Math.round((completedAssignments.length / actionableAssignments.length) * 100) : 0}%</span>
                                <span className="text-[6px] lg:text-[8px] font-black uppercase tracking-widest text-sky-200 text-center">Tiến độ</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="absolute -top-24 -right-24 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
                <div className="absolute -bottom-16 -left-16 w-48 h-48 bg-sky-400/10 rounded-full blur-2xl" />
            </section>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
                {/* Left Side: Tasks & Learning */}
                <div className="lg:col-span-8 xl:col-span-9 space-y-8">
                    {announcementAssignments.length > 0 && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <h3 className="text-base lg:text-lg font-black text-slate-800 uppercase tracking-widest flex items-center gap-3 text-left">
                                    <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center">
                                        <BellIcon className="h-5 w-5 text-violet-600" />
                                    </div>
                                    Bảng tin & Hoạt động
                                </h3>
                                <span className="px-3 py-1 bg-violet-50 text-violet-600 rounded-full text-[9px] lg:text-[10px] font-black uppercase tracking-widest border border-violet-100">
                                    {announcementAssignments.filter(a => !isSubmissionSubmitted(a.status)).length} chưa xem
                                </span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {announcementAssignments.slice(0, 4).map(assignment => {
                                    const statusMeta = getAssignmentStatusMeta(assignment);
                                    const cls = classes.find(c => String(c.id) === String(assignment.classId || (assignment as any).class_id));
                                    const dueDateText = assignment.dueDate ? new Date(assignment.dueDate).toLocaleDateString('vi-VN') : null;

                                    return (
                                        <div key={assignment.id} className={`p-5 rounded-[1.5rem] border transition-all text-left ${statusMeta.cardClass} hover:border-violet-200 shadow-sm`}>
                                            <div className="flex justify-between items-start gap-4">
                                                <div className="min-w-0 flex-1 flex gap-3">
                                                    <div className="w-8 h-8 shrink-0 rounded-lg flex items-center justify-center bg-violet-100 text-violet-600">
                                                        <BellIcon className="h-4 w-4" />
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <h5 className="font-bold text-slate-800 truncate text-left">{assignment.title}</h5>
                                                            <span className={`shrink-0 px-2 py-0.5 rounded text-[8px] font-black uppercase ${statusMeta.badgeClass}`}>{statusMeta.label}</span>
                                                        </div>
                                                        <p className="text-[10px] text-slate-400 font-medium uppercase tracking-tighter mb-2 flex items-center gap-1">
                                                            Lớp: {assignment.className}
                                                            {dueDateText ? ` • ${dueDateText}` : ''}
                                                        </p>
                                                        {assignment.instructions && (
                                                            <p className="mt-2 text-xs text-slate-500 line-clamp-2 italic text-left">"{assignment.instructions}"</p>
                                                        )}
                                                        <div className="flex gap-2 mt-3">
                                                            <button
                                                                onClick={() => {
                                                                    const sId = resolvedStudentProfile?.id || ((student as any)?.studentId || (student as any)?.email);
                                                                    onNavigate(View.AssignmentDetail, { assignmentId: assignment.id, title: assignment.title, classCode: cls?.inviteCode, studentId: sId });
                                                                }}
                                                                className="px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all bg-violet-50 text-violet-600 border border-violet-100 hover:bg-violet-100"
                                                            >
                                                                Xem thông báo
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Today's Priority Section */}
                    {activeAssignments.length > 0 && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <h3 className="text-base lg:text-lg font-black text-slate-800 uppercase tracking-widest flex items-center gap-3 text-left">
                                    <div className="w-8 h-8 bg-rose-100 rounded-lg flex items-center justify-center">
                                        <ClockIcon className="h-5 w-5 text-rose-600" />
                                    </div>
                                    Nhiệm vụ ưu tiên
                                </h3>
                                <span className="px-3 py-1 bg-rose-50 text-rose-600 rounded-full text-[9px] lg:text-[10px] font-black uppercase tracking-widest border border-rose-100">
                                    {activeAssignments.length} bài tập chưa làm
                                </span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-6">
                                {activeAssignments.slice(0, 4).map(assignment => {
                                    const isTask = (assignment as any).type === 'task';
                                    const submission = getLatestSubmissionForCurrentStudent(String(assignment.id));
                                    const isSubmitted = submission && isSubmissionSubmitted(submission.status);
                                    
                                    return (
                                        <div key={assignment.id} className="relative group bg-white rounded-[2rem] border border-slate-100 p-6 shadow-sm hover:shadow-xl hover:border-sky-200 transition-all overflow-hidden text-left">
                                            <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-sky-500/5 to-indigo-500/5 rounded-bl-[4rem] group-hover:scale-150 transition-transform duration-500" />
                                            <div className="relative space-y-4">
                                                <div className="flex justify-between items-start">
                                                    <div className="flex items-center gap-2">
                                                    <div className={`w-8 h-8 md:w-10 md:h-10 shrink-0 rounded-2xl flex items-center justify-center transition-all shadow-sm ${
                                                        isTask ? 'bg-emerald-50 text-emerald-600 group-hover:bg-emerald-600' : 
                                                        'bg-sky-50 text-sky-600 group-hover:bg-sky-600'
                                                    } group-hover:text-white`}>
                                                        {isTask ? <CheckBadgeIcon className="h-4 w-4 md:h-5 md:w-5" /> : <ClipboardDocumentListIcon className="h-4 w-4 md:h-5 md:w-5" />}
                                                    </div>
                                                        {isSubmitted && (
                                                            <span className="px-2 py-0.5 bg-emerald-50 text-emerald-600 text-[8px] font-black uppercase rounded-full border border-emerald-100">Đã nộp bài</span>
                                                        )}
                                                        {assignment.isRequired && (
                                                            <span className="px-2 py-0.5 bg-rose-50 text-rose-600 text-[8px] font-black uppercase rounded-full border border-rose-100">Bắt buộc</span>
                                                        )}
                                                    </div>
                                                    <span className="text-[10px] font-black text-rose-500 bg-rose-50 px-2.5 py-1 rounded-lg border border-rose-100 uppercase tracking-tighter">
                                                        Hạn: {new Date(assignment.dueDate).toLocaleDateString('vi-VN')}
                                                    </span>
                                                </div>
                                                <div>
                                                    <h4 className="font-black text-slate-800 text-lg leading-tight group-hover:text-sky-700 transition-colors text-left mb-1">{assignment.title}</h4>
                                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-1">
                                                        <AcademicCapIcon className="h-3 w-3" /> {(assignment as any).className}
                                                    </p>
                                                    {assignment.instructions && (
                                                        <p className="mt-2 text-xs text-slate-500 line-clamp-2 italic text-left">"{assignment.instructions}"</p>
                                                    )}
                                                </div>
                                                <div className="flex items-center justify-between text-[10px] font-bold text-slate-400">
                                                    <span className="flex items-center gap-1"><CalendarIcon className="h-3 w-3" /> {assignment.timeLimitMinutes || 'Không'} phút làm bài</span>
                                                    <span className="flex items-center gap-1 uppercase tracking-widest"><FlagIcon className="h-3 w-3" /> Điểm đạt: {assignment.passingGrade || '---'}</span>
                                                </div>
                                        <button 
                                            onClick={() => {
                                                        const hwId = assignment.homeworkId || assignment.id;
                                                        let hw = homeworks.find(h => String(h.id) === String(hwId));
                                                        const cls = classes.find(c => String(c.id) === String(assignment.classId || (assignment as any).class_id));
                                                        
                                                        // Fallback: Use embedded homework data from assignment if lookup fails
                                                        if (!hw && (assignment as any).homeworkTasks) {
                                                            hw = {
                                                                id: String(hwId),
                                                                title: (assignment as any).homeworkTitle || (assignment as any).title,
                                                                tasks: (assignment as any).homeworkTasks,
                                                                generalInstructions: (assignment as any).homeworkGeneralInstructions,
                                                                content: (assignment as any).homeworkContent
                                                            } as Homework;
                                                        }

                                                        const title = assignment.title || (assignment as any).homeworkTitle || (assignment as any).homework_title || hw?.title || (isTask ? 'Nhiệm vụ' : 'Bài tập');
                                                        const sId = resolvedStudentProfile?.id || ((student as any)?.studentId || (student as any)?.email);
                                                        onNavigate(View.AssignmentDetail, { assignmentId: assignment.id, title: assignment.title, classCode: cls?.inviteCode, studentId: sId });
                                                    }}
                                                    className={`w-full py-3 rounded-2xl text-[11px] font-black uppercase tracking-[0.2em] transition-all flex items-center justify-center gap-2 ${
                                                        (assignment.canSubmit || isSubmitted) 
                                                            ? (isSubmitted ? 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-emerald-100' : 'bg-sky-600 text-white hover:bg-sky-700 shadow-lg shadow-sky-100')
                                                            : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                                                    } shadow-lg group-hover:translate-y-[-2px] active:scale-95`}
                                                >
                                                    {isSubmitted ? 'Xem lại bài đã nộp' : (assignment.isExpired && !assignment.allowLateSubmission ? 'Đã quá hạn' : (
                                                        <>
                                                            <PlayIcon className="h-4 w-4" />
                                                            Bắt đầu ngay
                                                        </>
                                                    ))}
                                                </button>
                                    </div>
                                        </div>
                                    );
                                })}
                                        </div>
                                    </div>
                                )}

                    {/* All Assignments Section - Improved Grid */}
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-black text-slate-800 uppercase tracking-widest flex items-center gap-3 text-left">
                                <div className="w-8 h-8 bg-sky-100 rounded-lg flex items-center justify-center text-sky-600">
                                    <ClipboardDocumentListIcon className="h-5 w-5" />
                                </div>
                                Tất cả bài tập & Nhiệm vụ
                            </h3>
                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {actionableAssignments.length === 0 ? (
                                <div className="md:col-span-2 py-12 text-center bg-white rounded-[2rem] border-2 border-dashed border-slate-100">
                                    <p className="text-slate-400 font-bold uppercase tracking-widest text-xs text-center">Chưa có dữ liệu bài tập</p>
                                                </div>
                            ) : (
                                actionableAssignments.map(assignment => {
                                    const isTask = (assignment as any).type === 'task';
                                    const submission = getLatestSubmissionForCurrentStudent(String(assignment.id));
                                    const isSubmitted = submission && isSubmissionSubmitted(submission.status);
                                    const grade = submission?.grade;
                                    const statusMeta = getAssignmentStatusMeta(assignment);
                                    
                                    return (
                                        <div key={assignment.id} className={`p-5 rounded-[1.5rem] border transition-all text-left ${
                                            statusMeta.cardClass.includes('emerald') ? 'bg-slate-50 border-emerald-100' : 'bg-white border-slate-100 hover:border-sky-200 shadow-sm'
                                        }`}>
                                            <div className="flex justify-between items-start gap-4">
                                                <div className="min-w-0 flex-1 flex gap-3">
                                                    <div className={`w-8 h-8 shrink-0 rounded-lg flex items-center justify-center ${
                                                        isSubmitted ? 'bg-emerald-500 text-white' : (
                                                            isTask ? 'bg-emerald-100 text-emerald-600' :
                                                            'bg-sky-100 text-sky-600'
                                                        )
                                                    }`}>
                                                        {isSubmitted ? <TrophyIcon className="h-4 w-4" /> : (isTask ? <CheckBadgeIcon className="h-4 w-4" /> : <ClipboardDocumentListIcon className="h-4 w-4" />)}
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <h5 className="font-bold text-slate-800 truncate text-left">{assignment.title}</h5>
                                                            {grade !== undefined && grade !== null ? (
                                                                <span className="shrink-0 px-2 py-0.5 rounded bg-emerald-500 text-white text-[8px] font-black uppercase tracking-wider">{grade}/10</span>
                                                            ) : (
                                                                <span className={`shrink-0 px-2 py-0.5 rounded text-[8px] font-black uppercase ${statusMeta.badgeClass}`}>{statusMeta.label}</span>
                                                            )}
                                                        </div>
                                                        <p className="text-[10px] text-slate-400 font-medium uppercase tracking-tighter mb-2 flex items-center gap-1">
                                                            Lớp: {assignment.className} • Hạn: {new Date(assignment.dueDate).toLocaleDateString('vi-VN')}
                                                        </p>
                                                        {isSubmissionGraded(submission?.status) && (
                                                            <p className="text-[9px] text-emerald-600 font-bold bg-emerald-50 px-2 py-1 rounded-md mb-2 flex items-center gap-1">
                                                                <BellIcon className="h-3 w-3" /> GV đã chấm điểm & nhận xét
                                                            </p>
                                                        )}
                                                        <div className="flex gap-2">
                                                            <button 
                                                                onClick={() => {
                                                                    const cls = classes.find(c => String(c.id) === String(assignment.classId || (assignment as any).class_id));
                                                                    const sId = resolvedStudentProfile?.id || ((student as any)?.studentId || (student as any)?.email);
                                                                    onNavigate(View.AssignmentDetail, { assignmentId: assignment.id, title: assignment.title, classCode: cls?.inviteCode, studentId: sId });
                                                                }}
                                                                className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${
                                                                    isSubmitted 
                                                                        ? 'bg-emerald-50 text-emerald-600 border border-emerald-100 hover:bg-emerald-100' 
                                                                        : 'bg-sky-50 text-sky-600 border border-sky-100 hover:bg-sky-100'
                                                                }`}
                                                            >
                                                                {isSubmitted ? 'Xem lại bài' : 'Làm bài'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>

                {/* Right Side: Quick Info & Goals */}
                <div className="lg:col-span-4 xl:col-span-3 space-y-6 lg:space-y-8">
                    {/* User Profile Card */}
                    <Card className="overflow-hidden border-none shadow-xl bg-white rounded-[2rem] lg:rounded-[2.5rem]">
                        <div className="h-16 lg:h-20 bg-gradient-to-r from-sky-500 to-indigo-600 p-4 lg:p-6 flex justify-end">
                            <div className="w-7 h-7 lg:w-9 lg:h-9 bg-white/20 backdrop-blur-md rounded-xl flex items-center justify-center border border-white/20 text-white shadow-sm">
                                <UserCircleIcon className="h-4 w-4 lg:h-5 lg:w-5 text-white" />
                            </div>
                        </div>
                        <div className="px-6 lg:px-8 pb-6 lg:pb-8 -mt-9 lg:-mt-10 text-center">
                            <div className="relative inline-block mb-3">
                                <div className="w-18 h-18 lg:w-20 lg:h-20 bg-white rounded-[1.6rem] lg:rounded-[1.8rem] p-1.5 shadow-2xl ring-4 ring-white">
                                    <div className="w-full h-full bg-gradient-to-br from-sky-400 to-indigo-500 rounded-[1.2rem] lg:rounded-[1.4rem] flex items-center justify-center text-xl lg:text-2xl font-black text-white shadow-inner">
                                        {(student.teacherName || student.email || 'S').charAt(0)}
                                    </div>
                                </div>
                                <div className="absolute -bottom-0.5 -right-0.5 w-6 h-6 lg:w-7 lg:h-7 bg-emerald-500 rounded-2xl flex items-center justify-center border-4 border-white shadow-lg">
                                    <div className="w-1 h-1 lg:w-1.5 lg:h-1.5 bg-white rounded-full animate-ping" />
                                </div>
                            </div>
                            <h4 className="text-base lg:text-lg font-black text-slate-800 tracking-tight leading-none mb-1 text-center truncate px-2">{student.teacherName || student.email}</h4>
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] mb-6 text-center">Thành viên năng nổ</p>
                            
                            <div className="grid grid-cols-2 gap-2 lg:gap-3 mb-6">
                                <div className="p-2.5 bg-slate-50 rounded-2xl border border-slate-100/50 text-center shadow-sm">
                                    <p className="text-[7px] lg:text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1 leading-none text-center text-left">Đã xong</p>
                                    <p className="text-sm lg:text-base font-black text-sky-600 leading-none text-center">{completedAssignments.length}</p>
                                </div>
                                <div className="p-2.5 bg-slate-50 rounded-2xl border border-slate-100/50 text-center shadow-sm">
                                    <p className="text-[7px] lg:text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1 leading-none text-center text-left">Đang học</p>
                                    <p className="text-sm lg:text-base font-black text-indigo-600 leading-none text-center">{activeAssignments.length}</p>
                                </div>
                            </div>

                            <button onClick={() => navigate('/cai-dat')} className="w-full py-2.5 bg-slate-800 text-white text-[9px] font-black uppercase tracking-widest rounded-xl hover:bg-slate-900 transition-all shadow-lg active:scale-95">Thiết lập tài khoản</button>
                        </div>
                    </Card>

                    {/* Personal Goals Optimization */}
                    <Card className="p-6 lg:p-7 border-none shadow-xl bg-white rounded-[2rem] lg:rounded-[2.5rem]">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-[10px] lg:text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2 text-left">
                                <div className="w-1 h-3.5 bg-sky-500 rounded-full" />
                                Mục tiêu của tôi
                            </h3>
                            <button onClick={() => setIsEditingGoals(!isEditingGoals)} className="p-2 bg-slate-50 hover:bg-sky-50 rounded-xl text-slate-400 hover:text-sky-600 transition-all">
                                {isEditingGoals ? <XMarkIcon className="h-3.5 w-3.5" /> : <PencilIcon className="h-3.5 w-3.5" />}
                                </button>
                        </div>
                        <div className="space-y-5">
                            <div className="group text-left">
                                <div className="text-[8px] lg:text-[9px] font-black text-sky-500 uppercase tracking-widest mb-2 flex items-center gap-1.5 text-left">
                                    <div className="w-1 h-1 bg-sky-500 rounded-full" />
                                    Ngắn hạn
                                </div>
                                {isEditingGoals ? (
                                    <input type="text" value={shortTerm} onChange={e => setShortTerm(e.target.value)} placeholder="Ví dụ: Hoàn thành 5 bài tập tuần này" className="w-full text-xs font-bold text-slate-700 bg-slate-50 rounded-xl p-3 border-2 border-transparent focus:border-sky-200 outline-none transition-all" />
                                ) : (
                                    <p className="text-xs font-bold text-slate-700 bg-slate-50/50 rounded-xl p-3 border border-slate-100 group-hover:bg-sky-50/30 transition-colors leading-relaxed text-left min-h-[3rem]">
                                        {shortTerm || <span className="text-slate-300 italic font-medium">Mục tiêu tuần này?</span>}
                                    </p>
                                )}
                            </div>
                            <div className="group text-left">
                                <div className="text-[8px] lg:text-[9px] font-black text-indigo-500 uppercase tracking-widest mb-2 flex items-center gap-1.5 text-left">
                                    <div className="w-1 h-1 bg-indigo-500 rounded-full" />
                                    Dài hạn
                                </div>
                                {isEditingGoals ? (
                                    <input type="text" value={longTerm} onChange={e => setLongTerm(e.target.value)} placeholder="Ví dụ: Đạt điểm giỏi môn Tiếng Anh" className="w-full text-xs font-bold text-slate-700 bg-slate-50 rounded-xl p-3 border-2 border-transparent focus:border-sky-200 outline-none transition-all" />
                                ) : (
                                    <p className="text-xs font-bold text-slate-700 bg-slate-50/50 rounded-xl p-3 border border-slate-100 group-hover:bg-indigo-50/30 transition-colors leading-relaxed text-left min-h-[3rem]">
                                        {longTerm || <span className="text-slate-300 italic font-medium">Ước mơ của bạn?</span>}
                                    </p>
                                )}
                                            </div>
                            {isEditingGoals && (
                                <button onClick={handleSaveGoals} className="w-full py-2.5 bg-sky-600 text-white text-[9px] font-black uppercase tracking-widest rounded-xl shadow-xl shadow-sky-100 hover:bg-sky-700 transition-all active:scale-95">Lưu mục tiêu</button>
                            )}
                                            </div>
                    </Card>

                    {/* Join Class Request Optimization */}
                    {student.authType !== 'student_code' && (
                        <Card className="p-6 lg:p-7 border-none shadow-xl bg-white rounded-[2rem] lg:rounded-[2.5rem]">
                            <div className="flex justify-between items-center mb-6">
                                <div className="flex items-center gap-2.5">
                                    <div className="w-9 h-9 bg-amber-100 rounded-xl flex items-center justify-center text-amber-600 shadow-sm">
                                        <UserPlusIcon className="h-4.5 w-4.5" />
                                    </div>
                                    <div className="min-w-0 flex-1 text-left">
                                        <h3 className="text-[11px] lg:text-xs font-black text-slate-800 uppercase tracking-widest truncate">Lớp học kết nối</h3>
                                        <p className="text-[8px] lg:text-[9px] text-slate-400 font-bold uppercase tracking-tighter truncate">Tham gia cùng thầy cô</p>
                                    </div>
                                </div>
                                <button onClick={() => navigate('/tham-gia-lop')} className="p-2 bg-amber-50 hover:bg-amber-100 rounded-xl text-amber-600 transition-all active:scale-90 shadow-sm shrink-0">
                                    <PlusIcon className="h-3.5 w-3.5" />
                                </button>
                            </div>
                            {joinRequests.length === 0 ? (
                                <div className="text-center py-6 bg-slate-50 rounded-[2rem] border border-dashed border-slate-200">
                                    <p className="text-[9px] text-slate-400 font-black uppercase tracking-widest leading-relaxed px-4 text-left">Chưa kết nối lớp nào</p>
                                    <button onClick={() => navigate('/tham-gia-lop')} className="mt-3 px-3 py-1.5 bg-amber-500 text-white text-[9px] font-black uppercase rounded-lg hover:bg-amber-600 transition-all shadow-lg shadow-amber-100">Tìm lớp ngay</button>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {joinRequests.slice(0, 4).map((r: any) => {
                                        const isApproved = r.status === 'approved';
                                        const isRejected = r.status === 'rejected';
                                        const reason = r.rejectReason || r.reject_reason;
                                        return (
                                            <div key={r.id} className="space-y-1">
                                                <div className={`flex items-center justify-between p-2.5 rounded-xl border transition-all ${isApproved ? 'bg-emerald-50/50 border-emerald-100 group hover:border-emerald-200' : isRejected ? 'bg-rose-50/50 border-rose-100 group hover:border-rose-200' : 'bg-amber-50/50 border-amber-100 group hover:border-amber-200'}`}>
                                                    <div className="flex items-center gap-2.5 min-w-0 flex-1">
                                                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${isApproved ? 'bg-emerald-100 text-emerald-600' : isRejected ? 'bg-rose-100 text-rose-600' : 'bg-amber-100 text-amber-600'}`}>
                                                            <AcademicCapIcon className="h-3.5 w-3.5" />
                                                        </div>
                                                        <p className="text-[10px] font-bold text-slate-700 truncate">{r.class_name || r.className}</p>
                                                    </div>
                                                    <div className={`px-2 py-0.5 rounded-full text-[7px] font-black uppercase tracking-widest shrink-0 ${isApproved ? 'bg-emerald-500 text-white shadow-sm' : isRejected ? 'bg-rose-500 text-white shadow-sm' : 'bg-amber-500 text-white shadow-sm animate-pulse'}`}>
                                                        {isApproved ? 'Xong' : isRejected ? 'Từ chối' : 'Chờ'}
                                                    </div>
                                                </div>
                                                {isRejected && reason && (
                                                    <div className="px-3 py-1.5 bg-rose-50/80 rounded-lg text-[8px] font-medium text-rose-700 border border-rose-100/50 italic leading-relaxed">
                                                        Lý do: {reason}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                    {joinRequests.length > 4 && (
                                        <button onClick={() => navigate('/tham-gia-lop')} className="w-full py-2 text-[8px] font-black text-slate-400 uppercase tracking-[0.2em] hover:text-amber-600 transition-colors">Tất cả ({joinRequests.length})</button>
                                    )}
                                </div>
                            )}
                        </Card>
                    )}
                    </div>
            </div>

            {/* AI Learning Path Visual - Optimized */}
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <h3 className="text-xl lg:text-2xl font-black text-slate-800 uppercase tracking-widest flex items-center gap-3.5 text-left">
                        <div className="w-8 h-8 lg:w-9 lg:h-9 bg-indigo-100 rounded-xl lg:rounded-2xl flex items-center justify-center text-indigo-600">
                            <SparklesIcon className="h-4.5 w-4.5 lg:h-5 lg:w-5" />
                        </div>
                        Lộ trình phát triển bản thân
                    </h3>
                    {student.authType !== 'student_code' && (
                        <button onClick={() => setIsPlanModalOpen(true)} className="px-5 py-2.5 bg-indigo-600 text-white rounded-xl lg:rounded-2xl text-[10px] lg:text-[11px] font-black uppercase tracking-widest hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100 active:scale-95 flex items-center gap-2">
                            <PlusIcon className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
                            Tạo lộ trình AI
                            </button>
                    )}
                </div>
                
                {learningPlans.length === 0 ? (
                    <Card className="p-12 lg:p-16 text-center border-none shadow-xl bg-white rounded-[2.5rem] lg:rounded-[3rem] group cursor-pointer hover:shadow-2xl transition-all" onClick={() => student.authType !== 'student_code' && setIsPlanModalOpen(true)}>
                        <div className="w-20 h-20 lg:w-24 lg:h-24 bg-indigo-50 rounded-[2rem] lg:rounded-[2.5rem] flex items-center justify-center mx-auto mb-6 group-hover:scale-110 group-hover:bg-indigo-600 transition-all duration-500">
                            <SparklesIcon className="h-10 w-10 lg:h-12 lg:w-12 text-indigo-600 group-hover:text-white transition-colors" />
                        </div>
                        <h4 className="text-lg lg:text-xl font-black text-slate-800 mb-2 text-center">Hãy để AI giúp bạn thiết kế việc học!</h4>
                        <p className="text-slate-400 font-medium max-w-sm mx-auto text-center text-sm">Tạo lộ trình học tập riêng biệt cho môn học bạn yêu thích chỉ với một cú chạm.</p>
                    </Card>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {learningPlans.map(plan => (
                            <div key={plan.id} className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm hover:shadow-xl hover:translate-y-[-4px] transition-all overflow-hidden p-7 lg:p-8 flex flex-col h-full group text-left">
                                <div className="flex justify-between items-start mb-6 text-left">
                                    <div className="w-9 h-9 lg:w-10 lg:h-10 bg-indigo-50 rounded-xl lg:rounded-2xl flex items-center justify-center group-hover:bg-indigo-600 transition-all duration-500 text-indigo-600 group-hover:text-white">
                                        <AcademicCapIcon className="h-4.5 w-4.5 lg:h-5 lg:w-5" />
                                    </div>
                                    <span className={`px-2.5 py-1 rounded-full text-[8px] lg:text-[9px] font-black uppercase tracking-widest ${
                                        plan.planStatus === PlanStatus.Completed ? 'bg-emerald-100 text-emerald-700' : 'bg-sky-100 text-sky-700'
                                    }`}>{plan.planStatus}</span>
                                </div>
                                <h4 className="text-lg lg:text-xl font-black text-slate-800 mb-2 group-hover:text-indigo-600 transition-colors leading-tight text-left">{plan.title}</h4>
                                <p className="text-[10px] lg:text-xs text-slate-400 font-bold uppercase tracking-widest mb-6 text-left">{plan.difficulty} • {plan.duration}</p>
                                
                                <div className="mt-auto space-y-4">
                                    <div className="flex items-center justify-between text-[9px] lg:text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                        <span>Tiến trình</span>
                                        <span>75%</span>
                                    </div>
                                    <div className="w-full h-1.5 lg:h-2 bg-slate-100 rounded-full overflow-hidden mb-6 shadow-inner">
                                        <div className="h-full bg-gradient-to-r from-indigo-500 to-sky-500 w-3/4 rounded-full" />
                                    </div>
                                    <button onClick={() => onNavigate(View.LearningModuleView, { planId: plan.id, moduleId: plan.dailyPlans[0]?.modules[0]?.id, plan, module: plan.dailyPlans[0]?.modules[0] })} className="w-full py-2.5 lg:py-3 bg-slate-50 text-indigo-600 text-[9px] lg:text-[10px] font-black uppercase tracking-widest rounded-xl lg:rounded-2xl hover:bg-indigo-600 hover:text-white transition-all shadow-sm">Tiếp tục học</button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* AI Toolbox - New Section for CV 3439 */}
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <h3 className="text-xl lg:text-2xl font-black text-slate-800 uppercase tracking-widest flex items-center gap-3.5 text-left">
                        <div className="w-8 h-8 lg:w-9 lg:h-9 bg-pink-100 rounded-xl lg:rounded-2xl flex items-center justify-center text-pink-600">
                            <SparklesIcon className="h-4.5 w-4.5 lg:h-5 lg:w-5" />
                        </div>
                        Thư viện công cụ AI (NLc - CV 3439)
                    </h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {aiTools.map((tool, index) => (
                        <Card 
                            key={index} 
                            className="p-6 hover:shadow-2xl hover:translate-y-[-4px] transition-all cursor-pointer border-none group bg-white rounded-[2rem]"
                            onClick={() => onNavigate(tool.view)}
                        >
                            <div className={`w-12 h-12 ${tool.color} rounded-2xl flex items-center justify-center text-white mb-4 shadow-lg group-hover:scale-110 transition-transform`}>
                                {tool.icon}
                            </div>
                            <h4 className="text-lg font-black text-slate-800 mb-1 text-left">{tool.name}</h4>
                            <p className="text-xs text-slate-500 font-medium text-left leading-relaxed">{tool.description}</p>
                            <div className="mt-4 flex items-center text-[10px] font-black text-sky-600 uppercase tracking-widest group-hover:gap-2 transition-all">
                                <span>Khám phá ngay</span>
                                <ArrowLeftIcon className="h-3 w-3 rotate-180" />
                            </div>
                        </Card>
                    ))}
                </div>
            </div>

            {/* Smart AI Assistant Floating Feature */}
            <Card className="p-8 bg-slate-900 text-white border-none shadow-2xl relative overflow-hidden rounded-[2.5rem] lg:rounded-[3rem]">
                <div className="relative z-10 flex flex-col md:flex-row items-center gap-6 lg:gap-8">
                    <div className="w-16 h-16 lg:w-20 lg:h-20 bg-teal-500/20 rounded-[1.8rem] lg:rounded-[2rem] flex items-center justify-center shrink-0 border border-teal-500/30">
                        <SparklesIcon className="h-8 w-8 lg:h-10 lg:w-10 text-teal-400 animate-pulse" />
                    </div>
                    <div className="flex-1 text-center md:text-left space-y-2">
                        <h4 className="text-xl lg:text-2xl font-black flex items-center justify-center md:justify-start gap-3">
                            Trợ lý học tập thông minh
                            <span className="px-2 py-0.5 bg-teal-500 text-slate-900 text-[9px] lg:text-[10px] font-black uppercase rounded-lg">Online</span>
                        </h4>
                        <p className="text-slate-400 font-medium text-base lg:text-lg max-w-2xl text-center md:text-left">
                            Bạn gặp khó khăn với bài tập? Hãy hỏi Trợ lý AI để được hướng dẫn hiểu bài qua câu hỏi gợi mở. 
                            <span className="text-teal-400"> Trợ lý giúp em tự tư duy, không giải bài hộ!</span>
                        </p>
                    </div>
                    <button 
                        onClick={() => setIsAssistantOpen(true)}
                        className="px-8 lg:px-10 py-3.5 lg:py-4 bg-teal-600 hover:bg-teal-500 text-white font-black text-xs lg:text-sm uppercase tracking-[0.2em] rounded-xl lg:rounded-2xl transition-all shadow-xl shadow-teal-900 active:scale-95 shrink-0"
                    >
                        Trò chuyện ngay
                    </button>
                </div>
                <div className="absolute top-0 right-0 w-64 h-64 bg-teal-500/5 rounded-full blur-[100px] -mr-32 -mt-32"></div>
                <div className="absolute bottom-0 left-0 w-64 h-64 bg-indigo-500/5 rounded-full blur-[100px] -ml-32 -mb-32"></div>
            </Card>

            {isAssistantOpen && <StudyAssistantModal onClose={() => setIsAssistantOpen(false)} />}
            {isPlanModalOpen && <LearningPlanModal student={student} onClose={() => setIsPlanModalOpen(false)} onSave={onSavePlan} onCreditUsage={onCreditUsage} />}
        </div>
    );
};

export default StudentDashboard;
